import { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { NavLink, useSearchParams } from "react-router";

import { getPossibleQueueMembers, getQueueStats, getQueues } from "@/api/stackApi";
import { HELP_LOGS_QUEUE_STATS } from "@/constants/HelpUrls";
import MessageStack from "@/design/components/MessageStack";
import Page from "@/design/layouts/Page";
import { toggleLogExpand } from "@/store/reducers/settings";
import { setHelpUrl } from "@/store/reducers/ui";
import formatDuration from "@/utils/formaty";
import useNow from "@/utils/hooks/useNow";
import Valkyrie, {
  viAlignContentCenter,
  viAlignContentEnd,
  viCalendar,
  viCircleXmark,
  viCompress,
  viExpand,
  viList,
  viListBarChart,
  viListClock,
} from "@sippy-platform/valkyrie";

import { Box, Button, Card, CircularProgress, Container, useColorScheme, Divider, Stack, Tooltip } from "@mui/material";
import { barY, defineChart, crosshair } from "@tanstack/charts";
import { Chart } from "@tanstack/charts/react";
import { scaleBand } from "@tanstack/charts/scales/band";
import { scaleLinear } from "@tanstack/charts/scales/linear";
import { stack } from "@tanstack/charts/stack";
import { tooltip } from "@tanstack/charts/tooltip";
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import {
  differenceInDays,
  differenceInSeconds,
  endOfDay,
  format,
  isValid,
  isWithinInterval,
  parseISO,
  startOfDay,
} from "date-fns";

import ClearFilterAction from "../_Components/ClearFilterAction";
import DateFilter from "../_Components/DateFilter";
import FilterBar from "../_Components/FilterBar";
import MultiFilterDropdown from "../_Components/MultiFilterDropdown";
import RefreshActions from "../_Components/RefreshActions";
import { AgentCard } from "./AgentCard";
import { ABANDONED, ANSWERED, ELSEWHERE, REJECTED, seriesOrder, useSeriesColors } from "./chartSeries";
import { SeriesToggle } from "./SeriesToggle";
import { StatCard } from "./StatCard";

export default function QueueLog() {
  const seriesColors = useSeriesColors();
  const { colorScheme } = useColorScheme();

  const dateFormat = "yyyy-MM-dd'T'HH:mm:ss.SSS";

  const [searchParams, setSearchParams] = useSearchParams();

  const [visibleSeries, setVisibleSeries] = useState(seriesOrder);

  const dispatch = useDispatch();

  const pbx = useSelector((state) => state.domain.pbx);
  const logSettings = useSelector((state) => state.settings.logs);

  useEffect(() => {
    dispatch(setHelpUrl(HELP_LOGS_QUEUE_STATS));
  }, [dispatch]);

  // Filter preparations
  const queuesQuery = useQuery({
    queryKey: [pbx.context, "queues"],
    queryFn: getQueues,
    refetchInterval: 10 * 1000 * 60,
  });
  const possibleQueueMembers = useQuery({
    queryKey: [pbx.context, "queues.members"],
    queryFn: getPossibleQueueMembers,
    refetchInterval: 10 * 1000 * 60,
  });

  // Filters
  const now = useNow();
  const { startDate, endDate, queues, extension, diverge } = useMemo(
    () => ({
      startDate:
        searchParams.get("startDate") && isValid(parseISO(searchParams.get("startDate")))
          ? searchParams.get("startDate")
          : format(startOfDay(now), dateFormat),
      endDate:
        searchParams.get("endDate") && isValid(parseISO(searchParams.get("endDate")))
          ? searchParams.get("endDate")
          : format(endOfDay(now), dateFormat),
      queues: searchParams.get("queues")
        ? searchParams
            .get("queues")
            .split(",")
            .map((queue) => Number(queue))
        : [],
      extension: searchParams.get("extension") || "",
      diverge: searchParams.get("diverge") ?? "true",
    }),
    [now, searchParams],
  );

  const isFiltering = useMemo(
    () =>
      !(
        startDate === format(startOfDay(now), dateFormat) &&
        endDate === format(endOfDay(now), dateFormat) &&
        queues.length === 0 &&
        extension === ""
      ),
    [now, startDate, endDate, queues.length, extension],
  );

  const setFilter = useCallback(
    (key, value, resetPage = true) => {
      searchParams.set(key, value);
      if (resetPage) searchParams.set("page", 0);

      setSearchParams(searchParams);
    },
    [searchParams, setSearchParams],
  );

  // Check if the selected date range is invalid (either more than 31 days + 1 hour (because DST) or non-positive duration)
  const invalidDateRange =
    differenceInSeconds(parseISO(endDate), parseISO(startDate)) > 31 * 24 * 60 * 60 + 60 * 60 ||
    differenceInSeconds(parseISO(endDate), parseISO(startDate)) <= 0;

  // Setup default selected queues, select 10 if there are up to 10 queues in the PBX
  useEffect(() => {
    if (queuesQuery.isFetched && queuesQuery.data.length <= 10 && queues.length === 0) {
      setFilter("queues", queuesQuery.data.map((queue) => queue.id).join(","));
    }
  }, [queues?.length, queuesQuery.data, queuesQuery.isFetched, setFilter]);

  // Queries
  const pauseRefresh = !isWithinInterval(now, {
    start: parseISO(startDate),
    end: parseISO(endDate),
  });
  const queueStatsQuery = useQuery({
    queryKey: [pbx.context, "stats.queue", startDate, endDate, queues, extension],
    queryFn: () =>
      getQueueStats({
        queues,
        startDate,
        endDate,
        extension,
      }),
    placeholderData: keepPreviousData,
    refetchInterval: pauseRefresh ? 0 : logSettings.refreshInterval * 1000 * 60,
    refetchIntervalInBackground: false,
    refetchOnWindowFocus: pauseRefresh || logSettings.refreshInterval === 0 ? false : true,
    retry: 0,
    enabled:
      isValid(parseISO(startDate)) &&
      isValid(parseISO(endDate)) &&
      differenceInDays(parseISO(endDate), parseISO(startDate)) <= 31 &&
      queues.length > 0 &&
      queues.length < 11,
  });

  const { memberTotals, globalTotals } = useMemo(() => {
    const globalTotals = {
      waitTime: 0,
      talkTime: 0,
      ringTime: 0,
      callers: 0,
    };
    const memberTotals = [];

    if (queueStatsQuery.data?.stats) {
      const agents = Object.keys(queueStatsQuery.data?.stats);

      agents.map((agent) => {
        // Gloabl data
        globalTotals.callers = queueStatsQuery.data.callers;
        globalTotals.waitTime = queueStatsQuery.data.waitTime;
        queueStatsQuery.data.stats[agent].map((queue) => {
          globalTotals.talkTime += queue.talkTime;
          globalTotals.ringTime += queue.ringTime;
        });

        // Per user data
        const userData = possibleQueueMembers.data?.find((user) => user.attributes.exten === agent.split("-")[2]);

        const agentTotals = {
          callers: 0,
          rejected: 0,
          abandoned: 0,
          answered: 0,
          elsewhere: 0,
          waitTime: 0,
          talkTime: 0,
          ringTime: 0,
        };

        queueStatsQuery.data.stats[agent].map((queue) => {
          agentTotals.callers += queue.callers;
          agentTotals.rejected += queue.rejected;
          agentTotals.abandoned += queue.abandonedQueue;
          agentTotals.elsewhere += queue.answeredElsewhere;
          agentTotals.answered += queue.answered;
          agentTotals.waitTime += queue.waitTime;
          agentTotals.talkTime += queue.talkTime;
          agentTotals.ringTime += queue.ringTime;
        });

        memberTotals.push({
          agent: agent,
          user: userData,
          totals: agentTotals,
          queues: queueStatsQuery.data.stats[agent],
        });
      });
    }

    return { memberTotals, globalTotals };
  }, [possibleQueueMembers.data, queueStatsQuery.data]);

  const agentChart = useMemo(() => {
    // Diverging pushes rejected/abandoned below the zero baseline.
    const sign = diverge === "true" ? -1 : 1;

    const agents = memberTotals.map((agent) => ({
      id: agent.agent,
      name: agent.user ? agent.user.attributes.name : agent.agent.split("-")[2],
      exten: agent.user ? agent.user.attributes.exten : "",
      outcomes: [
        { series: ANSWERED, value: agent.totals.answered },
        { series: ELSEWHERE, value: agent.totals.elsewhere },
        { series: REJECTED, value: sign * agent.totals.rejected },
        { series: ABANDONED, value: sign * agent.totals.abandoned },
      ].filter((row) => visibleSeries.includes(row.series)),
    }));

    const labels = new Map(agents.map((agent) => [agent.id, { name: agent.name, exten: agent.exten }]));
    const rows = agents.flatMap((agent) => agent.outcomes.map((row) => ({ agent: agent.id, ...row })));

    const sumSigned = (agent, wantPositive) =>
      agent.outcomes.reduce((total, row) => (row.value > 0 === wantPositive ? total + row.value : total), 0);
    const bandMax = Math.max(0, ...agents.map((agent) => sumSigned(agent, true)));
    const bandMin = Math.min(0, ...agents.map((agent) => sumSigned(agent, false)));

    // Headroom is only added to ends that carry data, so an unused end stays on the baseline.
    const headroom = (bandMax - bandMin || 1) * 0.1;
    const hasData = bandMax > 0 || bandMin < 0;

    // Pinning the nice'd domain lets the hover band span the full axis height.
    const yScale = scaleLinear()
      .domain(hasData ? [bandMin < 0 ? bandMin - headroom : 0, bandMax > 0 ? bandMax + headroom : 0] : [0, 1])
      .nice();
    const [yMin, yMax] = yScale.domain();

    return defineChart({
      scales: {
        x: {
          scale: () => scaleBand().paddingInner(0).paddingOuter(0),
          axis: {
            line: true,
            ticks: {
              size: 4,
              padding: 8,
              format: (d) => {
                const label = labels.get(String(d));
                return label?.exten ? `${label.name} · ${label.exten}` : (label?.name ?? String(d));
              },
            },
          },
        },
        y: {
          scale: scaleLinear().domain([yMin, yMax]),
          axis: {
            line: true,
            ticks: { count: 10, size: 4, padding: 8, format: (d) => String(Math.abs(d)) },
            label: "Calls",
          },
          nice: true,
          grid: true,
        },
      },
      focus: "group-x",
      focusRing: false,
      svgAnimation: { duration: 180, easing: "ease-out" },
      tooltip: {
        use: tooltip,
        anchor: "pointer",
        content: (points) => {
          const label = labels.get(points[0]?.datum.agent);
          const outcomes = points
            .filter((point) => point.markId !== "agent-highlight")
            .sort((left, right) => seriesOrder.indexOf(left.datum.series) - seriesOrder.indexOf(right.datum.series));
          const total = outcomes.reduce((sum, point) => sum + Math.abs(point.datum.value), 0);

          return {
            title: label?.exten ? `${label.name} \u00b7 ${label.exten}` : (label?.name ?? ""),
            rows: [
              ...outcomes.map((point) => ({
                label: String(point.group),
                value: String(Math.abs(point.datum.value)),
                color: point.color,
              })),
              { label: "Total", value: String(total) },
            ],
          };
        },
      },
      marks: [
        crosshair({
          x: {
            band: {
              fill: colorScheme === "dark" ? "#fff" : "#000",
              fillOpacity: 0.05,
            },
          },
          y: false,
        }),
        barY(rows, {
          x: "agent",
          y: "value",
          z: "series",
          color: "series",
          layout: stack({ offset: "diverging", order: seriesOrder }),
          inset: 8,
        }),
      ],
      color: {
        domain: seriesOrder,
        range: seriesColors,
      },
    });
  }, [diverge, colorScheme, memberTotals, seriesColors, visibleSeries]);

  const queueLogsUrl = useMemo(() => {
    const params = new URLSearchParams({
      startDate,
      endDate,
      queues,
    });

    return `/pbx/${pbx.context}/logs/queues?${params.toString()}`;
  }, [endDate, pbx.context, queues, startDate]);

  return (
    <Page sx={{ pt: 8, position: "relative" }}>
      <title>{`Queue stats \u00b7 ${pbx.name} \u00b7 ${import.meta.env.SIPPY_APP_TITLE}`}</title>

      <FilterBar allowExpand>
        <ClearFilterAction disabled={isFiltering} />
        <Divider orientation="vertical" flexItem sx={{ my: 1, mx: 0.5 }} />
        <DateFilter startDate={startDate} endDate={endDate} setFilter={setFilter} />
        <Divider orientation="vertical" flexItem sx={{ my: 1, mx: 0.5 }} />
        <MultiFilterDropdown
          id="queues"
          label={{ all: "0 queues", singular: "queue", plural: "queues" }}
          icon={viList}
          options={queuesQuery.data || []}
          value={queues}
          setValue={(value) => setFilter("queues", value.length ? value.join(",") : "")}
          getOptionLabel={(x) => x.name}
          getOptionLabelSecondary={(x) => x.exten}
          getOptionValue={(x) => x.id}
          getAvatarIcon={() => viList}
          searchKeys={["name", "exten"]}
          limit={10}
        />
        <Divider orientation="vertical" flexItem sx={{ my: 1, mx: 0.5 }} />
        <Button
          startIcon={<Valkyrie icon={diverge === "true" ? viAlignContentCenter : viAlignContentEnd} />}
          color="inherit"
          onClick={() => setFilter("diverge", diverge === "false" ? "true" : "false")}
        >
          {diverge === "true" ? "Diverging" : "Stacked"}
        </Button>
        <Tooltip title={logSettings.expand ? "Limit width" : "Expand"}>
          <Button color="inherit" onClick={() => dispatch(toggleLogExpand())} sx={{ fontSize: "1rem", px: 1.25 }}>
            <Valkyrie icon={logSettings.expand ? viExpand : viCompress} />
          </Button>
        </Tooltip>
        <Divider orientation="vertical" flexItem sx={{ my: 1, mx: 0.5 }} />
        <Button startIcon={<Valkyrie icon={viListClock} />} component={NavLink} to={queueLogsUrl} color="inherit">
          Logs
        </Button>

        <Box sx={{ flexGrow: 1 }} />
        <RefreshActions
          onClick={() => queueStatsQuery.refetch()}
          isRefreshing={queueStatsQuery.isFetching}
          isPaused={pauseRefresh}
        />
      </FilterBar>

      <Container maxWidth={logSettings.expand ? "auto" : "xl"} sx={{ pt: 2 }}>
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fill, minmax(min(22rem, 100%), 1fr))",
            gap: 1,
          }}
        >
          {queues.length === 0 ? (
            <MessageStack icon={<Valkyrie icon={viList} />} message="Select at least one queue to get started." />
          ) : invalidDateRange ? (
            <MessageStack icon={<Valkyrie icon={viCalendar} />} message="The current date range is not valid." />
          ) : queues.length >= 11 ? (
            <MessageStack icon={<Valkyrie icon={viList} />} message="You have to many queues selected." color="error" />
          ) : queueStatsQuery.isFetched && queueStatsQuery.data ? (
            <>
              {Object.keys(queueStatsQuery.data?.stats).length >= 1 ? (
                <Stack
                  sx={{
                    gap: 1,
                    display: "grid",
                    gridTemplateColumns: "subgrid",
                    gridColumn: "1 / -1",
                  }}
                >
                  <Box sx={{ containerType: "inline-size", gridColumn: "1 / -1" }}>
                    <Card>
                      <Box
                        sx={{
                          display: "grid",
                          gridTemplateColumns: {
                            "@": "repeat(2, 1fr)",
                            "@800": "repeat(4, 1fr)",
                            "@1600": "240px auto",
                          },
                          gridTemplateRows: {
                            "@": "auto auto 1",
                            "@800": "auto 1",
                            "@1600": "repeat(4, 1fr)",
                          },
                          gridTemplateAreas: {
                            "@": "'talk wait' 'ring callers' 'stats stats'",
                            "@800": "'talk wait ring callers' 'stats stats stats stats'",
                            "@1600": "'talk stats' 'wait stats' 'ring stats' 'callers stats'",
                          },
                          gap: "1px",
                          bgcolor: "divider",
                        }}
                      >
                        <StatCard
                          title="Talk time"
                          stat={formatDuration(globalTotals.talkTime)}
                          desc={`Avg ${formatDuration(globalTotals.talkTime / globalTotals.callers)}`}
                          sx={{ gridArea: "talk" }}
                        />
                        <StatCard
                          title="Wait time"
                          stat={formatDuration(globalTotals.waitTime)}
                          desc={`Avg ${formatDuration(globalTotals.waitTime / globalTotals.callers)}`}
                          sx={{ gridArea: "wait" }}
                        />
                        <StatCard
                          title="Ring time"
                          stat={formatDuration(globalTotals.ringTime)}
                          desc={`Avg ${formatDuration(globalTotals.ringTime / globalTotals.callers)}`}
                          sx={{ gridArea: "ring" }}
                        />
                        <StatCard stat={globalTotals.callers} title="Calls" sx={{ gridArea: "callers" }} />
                        <Box
                          sx={{
                            p: 1,
                            bgcolor: "background.card",
                            gridArea: "stats",
                          }}
                        >
                          <div className="p-1">
                            <SeriesToggle visibleSeries={visibleSeries} setVisibleSeries={setVisibleSeries} />
                          </div>
                          <Chart definition={agentChart} ariaLabel="Call outcomes per agent" height={360} />
                        </Box>
                      </Box>
                    </Card>
                  </Box>
                  {memberTotals?.map((agent, agentKey) => (
                    <AgentCard key={agentKey} agent={agent} visibleSeries={visibleSeries} />
                  ))}
                </Stack>
              ) : (
                <MessageStack icon={<Valkyrie icon={viListBarChart} />} message="No data for your current filters..." />
              )}
            </>
          ) : queueStatsQuery.isError ? (
            <MessageStack
              icon={<Valkyrie icon={viCircleXmark} />}
              color="error"
              message="We couldn't finish your request..."
              description={queueStatsQuery?.error?.message}
            />
          ) : (
            <MessageStack icon={<CircularProgress size={36} />} message="Getting your data..." />
          )}
        </Box>
      </Container>
    </Page>
  );
}
